import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  Flame,
  Leaf,
  Brain,
  HeartPulse,
  Apple,
  Activity,
  Users,
  Smartphone,
  Video,
  BookOpen,
  Trophy,
  ChevronDown,
  PlayCircle,
  Clock,
  Star,
} from "lucide-react";
import corassaAsset from "@/assets/speaker-hero-v2.webp.asset.json";
import pratoImg from "@/assets/oferta-prato.jpg";
import celulaImg from "@/assets/oferta-celula.jpg";

export const Route = createFileRoute("/oferta")({
  head: () => ({
    meta: [
      { title: "Método Saúde Máxima — Dr. Eduardo Corassa" },
      {
        name: "description",
        content:
          "Tenha saúde, energia e vitalidade cuidando da causa — não apenas dos sintomas. Acesso completo ao Método Saúde Máxima por R$ 997.",
      },
      { property: "og:title", content: "Método Saúde Máxima — Dr. Eduardo Corassa" },
      {
        property: "og:description",
        content:
          "De R$ 2.997 por R$ 997 · 12x de R$ 99,70 · 15 dias de garantia incondicional.",
      },
    ],
  }),
  component: OfertaPage,
});

/* ---------- Reveal-on-scroll wrapper ---------- */
function Reveal({
  children,
  delay = 0,
  className = "",
  as: As = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: React.ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const Component = As as any;
  return (
    <Component
      ref={ref as any}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
      className={[
        "transition-all duration-700 ease-out will-change-transform",
        visible ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-6 blur-[2px]",
        className,
      ].join(" ")}
    >
      {children}
    </Component>
  );
}

/* ---------- CTA buttons ---------- */
function ScrollCta({ children }: { children: ReactNode }) {
  return (
    <button
      onClick={() => {
        document.getElementById("preco")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      className="group relative inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 sm:px-9 sm:py-5 text-base sm:text-lg font-semibold text-deep cta-pulse"
      style={{ background: "var(--gradient-cta)" }}
    >
      <span className="relative z-10">{children}</span>
      <ArrowRight className="relative z-10 w-5 h-5 transition-transform group-hover:translate-x-1" />
    </button>
  );
}

function BuyCta({ children }: { children: ReactNode }) {
  return (
    <Link
      to="/aplicacao"
      className="group relative inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 sm:px-9 sm:py-5 text-base sm:text-lg font-semibold text-deep cta-pulse"
      style={{ background: "var(--gradient-cta)" }}
    >
      <span className="relative z-10">{children}</span>
      <ArrowRight className="relative z-10 w-5 h-5 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

/* ---------- Section heading ---------- */
function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-leaf-bright/30 bg-leaf/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-leaf-bright">
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

/* ---------- The Page ---------- */
function OfertaPage() {
  return (
    <main className="relative overflow-hidden">
      {/* Decorative background blobs */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 -left-20 w-[420px] h-[420px] rounded-full blur-3xl opacity-25"
             style={{ background: "radial-gradient(circle, var(--leaf-bright), transparent 60%)" }} />
        <div className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-20"
             style={{ background: "radial-gradient(circle, var(--gold), transparent 60%)" }} />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
             style={{ background: "radial-gradient(circle, var(--lime), transparent 60%)" }} />
      </div>

      {/* ===== BLOCO 1 — TOPO ===== */}
      <section className="relative px-5 sm:px-8 pt-10 sm:pt-16 pb-14 sm:pb-24">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-leaf-bright/30 bg-leaf/10 px-3 py-1.5 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-leaf-bright">
              <span className="live-dot" />
              Método Saúde Máxima
            </div>
          </Reveal>

          <Reveal delay={60}>
            <p className="mt-5 text-sm sm:text-base text-gold-soft/90 italic max-w-2xl mx-auto">
              Seja pra reverter um quadro, prevenir doenças ou simplesmente envelhecer com mais energia e disposição
            </p>
          </Reveal>

          <Reveal delay={120} as="h1">
            <span className="font-display block mt-5 text-[2rem] leading-[1.05] sm:text-5xl md:text-6xl tracking-tight text-cream text-balance">
              Tenha <em className="not-italic text-leaf-bright">Saúde</em>,{" "}
              <em className="not-italic text-leaf-bright">Energia</em> e{" "}
              <em className="not-italic text-leaf-bright">Vitalidade</em>
              <span className="block mt-3 text-gold text-[1.5rem] sm:text-4xl md:text-5xl">
                Cuidando da Causa, e Não Apenas dos Sintomas
              </span>
            </span>
          </Reveal>

          {/* VSL */}
          <Reveal delay={220}>
            <div className="relative mx-auto mt-8 sm:mt-10 max-w-3xl">
              <div aria-hidden className="absolute -inset-0.5 rounded-3xl blur-sm opacity-30"
                   style={{ background: "var(--gradient-cta)" }} />
              <div className="relative aspect-video rounded-2xl border border-leaf-bright/40 bg-deep/60 shadow-[var(--shadow-card)] overflow-hidden">
                <iframe
                  src="https://player.vimeo.com/video/1202370649?badge=0&autopause=0&player_id=0&app_id=58479"
                  title="Método Saúde Máxima — Vídeo de Apresentação"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={320}>
            <div className="mt-8 sm:mt-10 flex flex-col items-center gap-5">
              <ScrollCta>QUERO COMEÇAR PELO MÉTODO SAÚDE MÁXIMA</ScrollCta>
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="flex text-gold">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </span>
                  <span>+700 mil seguidores</span>
                </span>
                <span className="hidden sm:inline opacity-30">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-leaf-bright" />
                  15 dias de garantia
                </span>
                <span className="hidden sm:inline opacity-30">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <Trophy className="w-4 h-4 text-gold" />
                  19 anos de prática
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={440}>
            <div className="mt-12 flex flex-col items-center gap-2 text-muted-foreground/70">
              <span className="text-[10px] uppercase tracking-[0.25em]">Role para entender o método</span>
              <ChevronDown className="w-4 h-4 animate-bounce" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 2 — IDENTIFICAÇÃO ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <SectionEyebrow>Talvez você se reconheça aqui</SectionEyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-3xl sm:text-4xl text-cream mt-5">
              Você já fez exames demais e ouviu poucas respostas.
            </h2>
          </Reveal>
          <div className="mt-7 space-y-5 text-base sm:text-lg leading-relaxed text-muted-foreground">
            <Reveal delay={100}>
              <p>
                Já saiu de consultas de dez minutos com mais uma receita na mão e a mesma dúvida no peito:{" "}
                <span className="text-cream italic">por que ninguém me explica o que realmente está acontecendo comigo?</span>
              </p>
            </Reveal>
            <Reveal delay={180}>
              <p>
                Talvez seja <span className="text-leaf-bright">diabetes</span>. Pressão alta. Uma{" "}
                <span className="text-leaf-bright">doença autoimune</span> que vai e volta. Um cansaço que nenhum suplemento resolve. Quilos que não saem por mais que você tente. Ou o medo de ver na sua família a mesma história se repetindo.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <p className="border-l-2 border-leaf-bright pl-5 text-cream">
                E no fundo fica sempre a mesma sensação: a de que você está tratando o sintoma e nunca a origem.
              </p>
            </Reveal>
            <Reveal delay={340}>
              <p>
                Se isso fala com você, leia até o fim. O que vem agora pode mudar a forma como você enxerga a sua saúde.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== BLOCO 3 — A VIRADA ===== */}
      <section className="relative px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-center">
            <Reveal>
              <div className="rounded-2xl border border-border bg-card/70 backdrop-blur p-6 shadow-[var(--shadow-card)]">
                <Flame className="w-8 h-8 text-gold mb-3" />
                <p className="text-xs uppercase tracking-widest text-gold/80 mb-2">A causa silenciosa</p>
                <p className="text-cream">
                  Inflamação que não cede. Um intestino desequilibrado. Um corpo sobrecarregado de comida industrializada, açúcar e estímulos que ele nunca foi feito pra processar.
                </p>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="flex items-center justify-center text-leaf-bright">
                <ArrowRight className="w-8 h-8 hidden md:block" />
                <ChevronDown className="w-8 h-8 md:hidden animate-bounce" />
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="relative rounded-2xl border border-leaf-bright/40 bg-leaf/10 overflow-hidden" style={{ boxShadow: "var(--glow-gold)" }}>
                <img
                  src={celulaImg}
                  alt="Visualização científica de autofagia celular"
                  loading="lazy"
                  width={1280}
                  height={832}
                  className="w-full h-32 sm:h-40 object-cover opacity-80"
                />
                <div className="p-6">
                  <Trophy className="w-8 h-8 text-gold mb-3" />
                  <p className="text-xs uppercase tracking-widest text-leaf-bright/80 mb-2">A virada científica</p>
                  <p className="text-cream">
                    Em 2016, o <span className="text-leaf-bright font-semibold">Prêmio Nobel de Medicina</span> foi dado pela descoberta da <span className="italic">autofagia</span> — o mecanismo pelo qual a célula recicla o que está velho e danificado. E o que aciona esse mecanismo está diretamente ligado à forma como você come.
                  </p>
                </div>
              </div>
            </Reveal>


          </div>
          <Reveal delay={420}>
            <p className="text-center font-display text-2xl sm:text-3xl text-cream mt-12">
              Esse é o ponto de partida do <span className="text-leaf-bright">Método Saúde Máxima</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 4 — AUTORIDADE ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <Reveal><SectionEyebrow>Quem é</SectionEyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-3xl sm:text-5xl text-cream mt-4">Dr. Eduardo Corassa</h2>
          </Reveal>

          {/* Stats row */}
          <Reveal delay={140}>
            <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl">
              {[
                { n: "+700k", l: "Seguidores" },
                { n: "19", l: "Anos de prática" },
                { n: "10", l: "Livros publicados" },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl border border-border bg-card/60 backdrop-blur p-4 sm:p-5 text-center hover:border-leaf-bright/50 transition-colors">
                  <p className="font-display text-2xl sm:text-4xl text-leaf-bright leading-none">{s.n}</p>
                  <p className="mt-2 text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-[auto_1fr] gap-10 items-start">

            <Reveal delay={100}>
              <div className="relative mx-auto lg:mx-0 w-full max-w-xs">
                <div className="relative rounded-3xl overflow-hidden border border-leaf-bright/40 shadow-[var(--shadow-card)]">
                  <img
                    src={corassaAsset.url}
                    alt="Dr. Eduardo Corassa, Nutricionista Clínico"
                    loading="lazy"
                    width={600}
                    height={750}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="font-display text-xl text-cream">Dr. Eduardo Corassa</p>
                  <p className="text-xs text-gold uppercase tracking-widest mt-1">Nutricionista Clínico · CRN 17101115</p>
                </div>
              </div>
            </Reveal>

            <div className="space-y-8">
              <Reveal delay={180}>
                <div className="space-y-5 text-base sm:text-lg leading-relaxed text-muted-foreground">
                  <p>Antes de ser referência pra mais de <span className="text-cream font-semibold">700 mil pessoas</span>, o Dr. Corassa foi paciente.</p>
                  <p>
                    Criança obesa, chegou aos 22 anos com sintomas de diabetes e duas cirurgias marcadas. Peregrinou por dezenas de médicos, e a resposta era quase sempre a mesma: <em>é natural, é genética, você vai precisar de remédio pra vida toda.</em>
                  </p>
                  <p>
                    Ele não aceitou aquilo como sentença. Foi estudar a fundo a relação entre alimentação, fisiologia e doença — e começou a aplicar em si mesmo.{" "}
                    <span className="text-cream">Dezenove anos depois, o resultado é o melhor argumento que existe: nenhuma doença, nenhum medicamento, a fisiologia de um atleta.</span>
                  </p>
                </div>
              </Reveal>

              <Reveal delay={260}>
                <div className="rounded-2xl border border-border bg-card/70 backdrop-blur p-6 sm:p-7 shadow-[var(--shadow-card)]">
                  <p className="text-sm uppercase tracking-widest text-gold mb-4">Hoje, como Nutricionista Clínico:</p>
                  <ul className="space-y-3">
                    {[
                      "19 anos de prática clínica e +700 mil seguidores",
                      "10 livros publicados e 6 cursos online",
                      "Retiros presenciais em Saquarema (RJ)",
                      "Globo Repórter, Record TV, Rede Brasil, TEDx Talks e Revista dos Vegetarianos",
                    ].map((item) => (
                      <li key={item} className="flex gap-3 text-cream">
                        <CheckCircle2 className="w-5 h-5 text-leaf-bright shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-sm text-muted-foreground">
                    Tudo o que ele aprendeu nesse caminho está organizado, passo a passo, dentro do Método Saúde Máxima.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

        </div>
      </section>

      {/* ===== BLOCO 5 — O QUE É O MÉTODO ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal><SectionEyebrow>O Método</SectionEyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-3xl sm:text-5xl text-cream mt-4">
              Da confusão à clareza. Da clareza à prática.
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              O Saúde Máxima é um método completo que te conduz a cuidar da causa da sua saúde — e não só dos sintomas. Ele une o que a ciência da nutrição já demonstra (mais comida de verdade, menos ultraprocessado, cronobiologia, jejum bem orientado, microbiota e ativação dos mecanismos naturais de reparo) com um caminho simples de aplicar no seu dia a dia.
            </p>
          </Reveal>
          <Reveal delay={250}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {["4 Fases", "3 Aceleradores", "4 Multiplicadores"].map((t, i) => (
                <span
                  key={t}
                  className="px-5 py-2 rounded-full border border-leaf-bright/40 bg-leaf/10 text-leaf-bright font-medium"
                  style={{ animation: `float-soft 5s ease-in-out ${i * 0.4}s infinite` }}
                >
                  {t}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={350}>
            <div className="mt-14 relative rounded-3xl overflow-hidden border border-leaf/30 shadow-[var(--shadow-card)] max-w-3xl mx-auto">
              <img
                src={pratoImg}
                alt="Prato de comida real: vegetais, brotos, frutas e oleaginosas"
                loading="lazy"
                width={1280}
                height={832}
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/30 to-transparent" />
              <p className="absolute bottom-5 left-5 right-5 text-center text-cream font-display text-lg sm:text-2xl">
                Comida de verdade. Princípios simples. Resultados profundos.
              </p>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ===== BLOCO 6 — O QUE VOCÊ VAI RECEBER ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal><SectionEyebrow>O que você vai receber</SectionEyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-3xl sm:text-5xl text-cream mt-4">As 4 Fases do Método</h2>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              {
                n: "01",
                title: "Clareza",
                desc: "Você entende a jornada completa, define o seu objetivo de saúde e conhece a história e a lógica por trás do método. O ponto de partida pra saber exatamente onde está e pra onde vai.",
                icon: Sparkles,
              },
              {
                n: "02",
                title: "Fundamentos",
                desc: "A causa real por trás das doenças crônicas, o modelo higienista, os limites do tratar só com remédio e os pilares que sustentam a saúde de verdade. É aqui que a ficha cai.",
                icon: Brain,
              },
              {
                n: "03",
                title: "Prática",
                desc: "Mão na massa: culinária natural, escolha e armazenamento de alimentos, germinação, montagem do prato, combinação alimentar e +30 receitas práticas pra aplicar já na próxima refeição.",
                icon: Apple,
              },
              {
                n: "04",
                title: "Ciência",
                desc: "Aprofundamento: alimentos crus e AGEs, mecanismo da compulsão, jejum e autofagia (mTOR, IGF-1, AMPK e sirtuínas), ganhar peso de forma saudável e sinergia entre todos os pilares.",
                icon: Activity,
              },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <div className="group relative h-full rounded-2xl border border-border bg-card/70 backdrop-blur p-6 sm:p-7 overflow-hidden hover:border-leaf-bright/60 transition-all hover:-translate-y-1 hover:shadow-[var(--shadow-card)]">
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-3xl"
                       style={{ background: "var(--leaf-bright)" }} />
                  <span aria-hidden className="absolute top-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                       style={{ background: "var(--gradient-cta)" }} />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-display text-4xl sm:text-5xl text-gold/80 leading-none">{f.n}</span>
                      <div className="grid place-items-center w-10 h-10 rounded-xl border border-leaf-bright/30 bg-leaf/10">
                        <f.icon className="w-5 h-5 text-leaf-bright" />
                      </div>
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl text-cream">Fase {f.n.slice(1)} — {f.title}</h3>
                    <p className="mt-3 text-muted-foreground text-sm sm:text-base">{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Aceleradores */}
          <Reveal>
            <h3 className="font-display text-2xl sm:text-3xl text-cream mt-16">Os 3 Aceleradores</h3>
          </Reveal>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {[
              { icon: HeartPulse, title: "Protocolos por Objetivo", desc: "Caminhos específicos para saúde metabólica, cardiovascular, anti-inflamatória e neurológica. Você aplica o que faz sentido pro seu caso." },
              { icon: Sparkles, title: "IA de Receitas e Recomendações", desc: "Uma inteligência que monta receitas e orienta suas escolhas dentro do método, na palma da mão." },
              { icon: BookOpen, title: "Bônus", desc: "Curso de Sorvetes Naturais + E-book Guia do método." },
            ].map((a, i) => (
              <Reveal key={a.title} delay={i * 90}>
                <div className="h-full rounded-2xl border border-gold/30 bg-gradient-to-b from-card/80 to-card/40 p-6 hover:scale-[1.02] transition-transform">
                  <a.icon className="w-7 h-7 text-gold mb-3" />
                  <h4 className="font-display text-xl text-cream">{a.title}</h4>
                  <p className="mt-2 text-muted-foreground text-sm">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Multiplicadores */}
          <Reveal>
            <h3 className="font-display text-2xl sm:text-3xl text-cream mt-16">Os 4 Multiplicadores</h3>
          </Reveal>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Smartphone, title: "App Saúde Máxima com Ranking", desc: "Acompanhe sua evolução e se mantenha motivado." },
              { icon: Video, title: "Netflix de Depoimentos", desc: "Biblioteca de transformações reais pra você se inspirar." },
              { icon: Users, title: "Comunidade Saúde Máxima", desc: "Pessoas vivendo o mesmo processo, trocando receitas, dúvidas e apoio." },
              { icon: Leaf, title: "Aula Ao Vivo Mensal", desc: "Uma vez por mês, acesso direto ao Dr. Corassa pra tirar dúvidas e ver novidades da literatura." },
            ].map((m, i) => (
              <Reveal key={m.title} delay={i * 80}>
                <div className="h-full rounded-2xl border border-leaf/40 bg-deep/60 backdrop-blur p-6 hover:border-leaf-bright transition-colors">
                  <m.icon className="w-7 h-7 text-leaf-bright mb-3" />
                  <h4 className="font-display text-lg text-cream">{m.title}</h4>
                  <p className="mt-2 text-muted-foreground text-sm">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BLOCO 7 — DEPOIMENTOS ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <Reveal><SectionEyebrow>Provas reais</SectionEyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-3xl sm:text-5xl text-cream mt-4">
              Histórias de quem decidiu cuidar da causa
            </h2>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { id: "MKOx5g9VNk8", title: "Reverteu câncer e diabetes", desc: "Relato de transformação completa aplicando o método." },
              { id: "RLkrCU1g-aA", title: "Curou fibromialgia", desc: "Dores crônicas controladas pela alimentação e estilo de vida." },
              { id: "n9PJ87q_Ly4", title: "Curou pedra na vesícula e gordura no fígado", desc: "Marcadores normalizados sem cirurgia." },
              { id: "w95FCdGZjQg", title: "Saiu da obesidade e se livrou da pressão alta", desc: "Perda de peso saudável e fim dos remédios para hipertensão." },
              { id: "6y90igPYI1c", title: "Curou rinite, alergia e dermatite", desc: "Sintomas inflamatórios crônicos resolvidos pela raiz." },
            ].map((t, i) => (
              <Reveal key={t.id} delay={i * 70}>
                <div className="h-full rounded-2xl border border-border bg-card/70 backdrop-blur p-5 hover:border-gold/50 transition-colors">
                  <div className="flex gap-1 text-gold mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <div className="relative aspect-video rounded-xl overflow-hidden border border-border bg-deep/60 mb-4">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${t.id}?rel=0&modestbranding=1&showinfo=0&controls=1&iv_load_policy=3&fs=0&disablekb=1&playsinline=1&color=white`}
                      title={t.title}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      className="w-full h-full"
                    />
                    {/* Bloqueia clique no logo do YouTube (canto inferior direito) */}
                    <div className="absolute bottom-0 right-0 w-20 h-10 z-10" aria-hidden />
                    {/* Bloqueia clique no título (topo) que abre no YouTube */}
                    <div className="absolute top-0 left-0 right-0 h-10 z-10" aria-hidden />
                  </div>

                  <p className="text-cream font-medium">{t.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>


          <Reveal delay={200}>
            <p className="text-xs text-muted-foreground text-center mt-8 italic">
              Depoimentos reais, descritos como experiências individuais. Resultados variam de pessoa para pessoa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 8 — PARA QUEM É (E NÃO É) ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-5xl grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-leaf-bright/40 bg-leaf/10 p-6 sm:p-8">
              <h3 className="font-display text-2xl text-cream flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-leaf-bright" />
                É pra você que:
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  "Convive com uma doença crônica e quer entender a raiz, não só medicar o sintoma",
                  "Quer mais energia, melhor digestão, sono e disposição",
                  "Quer prevenir e envelhecer com saúde",
                  "Cuida de alguém da família e quer um caminho claro",
                  "Já tentou de tudo e quer um método organizado, do começo ao fim",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-cream">
                    <CheckCircle2 className="w-5 h-5 text-leaf-bright shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full rounded-2xl border border-border bg-card/70 backdrop-blur p-6 sm:p-8">
              <h3 className="font-display text-2xl text-cream">Talvez não seja o momento se você:</h3>
              <ul className="mt-5 space-y-3 text-muted-foreground">
                {[
                  "Busca uma pílula mágica ou resultado sem mudar nada na rotina",
                  "Não está disposto a colocar a prática em ação",
                  "Espera substituir o acompanhamento do seu médico (o método caminha junto, não no lugar)",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="text-gold mt-0.5">—</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 9 — ANCORAGEM E PREÇO ===== */}
      <section id="preco" className="relative px-5 sm:px-8 py-20 sm:py-28 scroll-mt-10">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="text-center">
              <SectionEyebrow>Valor real do método</SectionEyebrow>
              <h2 className="font-display text-3xl sm:text-5xl text-cream mt-4">
                Vamos colocar o valor em perspectiva
              </h2>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 rounded-2xl border border-border bg-card/70 backdrop-blur p-6 sm:p-8 shadow-[var(--shadow-card)]">
              <ul className="divide-y divide-border">
                {[
                  ["Método completo, nas 4 Fases", "R$ 1.997"],
                  ["Protocolos por Objetivo", "R$ 497"],
                  ["IA de Receitas e Recomendações", "R$ 397"],
                  ["App + Ranking + Comunidade", "R$ 594"],
                  ["Aula Ao Vivo Mensal com o Dr. Corassa", "R$ 1.997"],
                  ["Bônus: Curso de Sorvetes Naturais", "R$ 297"],
                  ["Bônus: E-book Guia", "R$ 97"],
                ].map(([label, price]) => (
                  <li key={label} className="flex justify-between gap-4 py-3 text-sm sm:text-base">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="text-cream font-medium tabular-nums">{price}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-border flex justify-between items-baseline">
                <span className="text-cream font-semibold">Valor real somado</span>
                <span className="font-display text-2xl text-gold">+ R$ 5.800</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={250}>
            <div className="mt-10 relative rounded-3xl p-[2px] overflow-hidden" style={{ background: "var(--gradient-cta)" }}>
              <div className="relative rounded-[calc(1.5rem-1px)] bg-deep p-7 sm:p-10 text-center overflow-hidden">
                <div aria-hidden className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full blur-3xl opacity-20"
                     style={{ background: "var(--leaf-bright)" }} />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/40 px-3 py-1 text-xs uppercase tracking-widest text-gold">
                    <Sparkles className="w-3 h-3" />
                    Economia de R$ 4.803
                  </div>
                  <p className="mt-4 text-sm uppercase tracking-widest text-gold">Condição especial</p>
                  <p className="mt-2 text-muted-foreground">
                    O valor normal sempre foi <span className="line-through">R$ 2.997</span>. Pra você, hoje:
                  </p>
                  <div className="mt-6 flex items-end justify-center gap-3 flex-wrap">
                    <span className="font-display text-5xl sm:text-7xl text-cream leading-none">R$ 997</span>
                    <span className="text-muted-foreground pb-2">à vista no Pix</span>
                  </div>
                  <p className="mt-3 text-leaf-bright font-medium">ou 12x de R$ 99,70 no cartão</p>

                  <div className="mt-8">
                    <BuyCta>QUERO GARANTIR MINHA VAGA POR R$ 997</BuyCta>
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground flex items-center justify-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-leaf-bright" />
                      15 dias de garantia
                    </span>
                    <span className="opacity-30">·</span>
                    <span>Compra 100% segura</span>
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ===== BLOCO 10 — GARANTIA ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <div className="rounded-3xl border border-gold/40 bg-gradient-to-br from-card/80 to-deep p-7 sm:p-10 flex flex-col md:flex-row items-center gap-8">
              <div className="shrink-0 relative">
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-2 border-gold flex flex-col items-center justify-center text-center"
                     style={{ boxShadow: "var(--glow-gold)" }}>
                  <ShieldCheck className="w-8 h-8 text-gold" />
                  <p className="font-display text-3xl text-cream mt-1 leading-none">15</p>
                  <p className="text-[10px] uppercase tracking-widest text-gold">dias</p>
                  <p className="text-xs text-cream">garantia</p>
                </div>
              </div>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl text-cream">Garantia incondicional</h3>
                <p className="mt-3 text-muted-foreground">
                  Entre, acesse o método inteiro, comece a aplicar. Se em até 15 dias você sentir que não é pra você, é só pedir o reembolso e devolvemos cada centavo. <span className="text-cream">O risco é todo nosso. A decisão de começar é sua.</span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 11 — ESCASSEZ ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-gold">
              <span className="live-dot" />
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium uppercase tracking-widest">Janela limitada</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl sm:text-5xl text-cream mt-5">
              Essa condição existe enquanto esta página estiver no ar.
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-5 text-muted-foreground text-base sm:text-lg">
              Encerrado o prazo, o valor volta para o preço definitivo de <span className="text-cream">R$ 2.997</span>. Cada dia adiando é mais um dia tratando sintoma em vez de cuidar da causa.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <div className="mt-9">
              <BuyCta>COMEÇAR AGORA POR R$ 997</BuyCta>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 12 — FAQ ===== */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl">
          <Reveal><SectionEyebrow>Perguntas frequentes</SectionEyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-3xl sm:text-5xl text-cream mt-4">Quebra de objeções</h2>
          </Reveal>

          <div className="mt-10 space-y-3">
            {[
              {
                q: "Preciso parar com meus remédios ou meu tratamento?",
                a: "Não. O método caminha junto com o seu acompanhamento de saúde. Qualquer ajuste de medicação deve ser feito sempre com o seu médico.",
              },
              {
                q: "Vou ter que virar vegano ou comer só cru?",
                a: "Não. Você aplica os pilares no seu ritmo, com escolhas práticas e progressivas. A Fase 3 te dá mais de 30 receitas pra começar leve.",
              },
              {
                q: "Tenho diabetes. Posso comer fruta?",
                a: "Esse é exatamente um dos pontos que o método esclarece, com base em fisiologia — sem os mitos que assustam.",
              },
              {
                q: "Não tenho tempo nem sou bom de cozinha.",
                a: "O método foi feito pra vida real: receitas simples, IA pra te ajudar a montar o prato e uma comunidade pra apoiar.",
              },
              {
                q: "Funciona pra prevenção, mesmo sem doença?",
                a: "Sim. Boa parte dos alunos entra justamente pra ter mais energia, melhorar marcadores e envelhecer com saúde.",
              },
              {
                q: "E se eu não gostar?",
                a: "Você tem 15 dias de garantia incondicional. Risco zero.",
              },
            ].map((item, i) => (
              <Reveal key={item.q} delay={i * 60}>
                <details className="group rounded-2xl border border-border bg-card/60 backdrop-blur overflow-hidden">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-5 sm:p-6 text-cream font-medium">
                    <span>{item.q}</span>
                    <ChevronDown className="w-5 h-5 text-leaf-bright shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground -mt-1">{item.a}</div>
                </details>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <div className="mt-10 text-center">
              <BuyCta>SIM, QUERO COMEÇAR</BuyCta>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BLOCO 13 — FECHAMENTO ===== */}
      <section className="px-5 sm:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl text-cream leading-tight">
              Você pode fechar esta página e seguir igual amanhã.
              <span className="block mt-3 text-leaf-bright">Ou pode dar o primeiro passo.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-6 text-muted-foreground text-base sm:text-lg">
              O Dr. Corassa percorreu esse caminho na própria pele. Milhares de pessoas vieram depois. O convite pra começar o seu está aqui.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-10 flex flex-col items-center gap-3">
              <BuyCta>QUERO O MÉTODO SAÚDE MÁXIMA POR R$ 997</BuyCta>
              <p className="text-xs sm:text-sm text-muted-foreground">
                De <span className="line-through opacity-70">R$ 2.997</span> por{" "}
                <span className="text-cream font-semibold">R$ 997</span> · 12x de R$ 99,70 · 15 dias de garantia
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== RODAPÉ ===== */}
      <footer className="px-5 sm:px-8 py-12 border-t border-border">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs text-muted-foreground leading-relaxed">
            O Método Saúde Máxima tem caráter educativo e não substitui consulta, diagnóstico ou tratamento médico. As informações apresentadas não constituem promessa de cura. Os relatos são experiências individuais e os resultados variam de pessoa para pessoa. Não interrompa nem altere qualquer tratamento ou medicação sem orientação do seu médico. Em caso de doença, mantenha o acompanhamento profissional adequado.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Método Saúde Máxima · Dr. Eduardo Corassa — Nutricionista Clínico CRN 17101115
          </p>
        </div>
      </footer>
    </main>
  );
}
